import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { getUnreadNotifications, markNotificationsAsRead } from '../utils/notificationService';
import { fetchUnreadArtifacts, markAsRead as markChatAsRead } from '../utils/utils';
import socket from '../service/socketService';

export interface GeneralNotification {
  id: number;
  from_user_id: string;
  to_user_id: string;
  message: string;
  title?: string;
  department?: string;
  comment?: string;
  quarter: number;
  financialYear: string;
  is_read: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ChatNotification {
  artifact_id: string;
  count: number;
  artifact_name: string;
  read: boolean;
}

export interface CombinedNotifications {
  generalNotifications: GeneralNotification[];
  chatNotifications: ChatNotification[];
  totalCount: number;
}

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<CombinedNotifications>({
    generalNotifications: [],
    chatNotifications: [],
    totalCount: 0,
  });
  const [loading, setLoading] = useState(false);

  const loggedUser = useSelector((state: any) => state.user?.employee);
  const currentUserId = localStorage.getItem('user_id') || loggedUser?.id;

  // Fetch all notifications (general + chat)
  const fetchAllNotifications = useCallback(async () => {
    if (!currentUserId) return;

    setLoading(true);
    try {
      // Fetch general notifications and chat notifications in parallel
      const [generalNotifications, chatNotifications] = await Promise.all([getUnreadNotifications(currentUserId), fetchUnreadArtifacts()]);

      const combinedNotifications = {
        generalNotifications: Array.isArray(generalNotifications) ? generalNotifications : [],
        chatNotifications: Array.isArray(chatNotifications) ? chatNotifications : [],
        totalCount: 0,
      };
      let count = 0;
      for (let i = 0; i < combinedNotifications.generalNotifications.length; i += 1) {
        if (combinedNotifications.generalNotifications[i]?.is_read === false) {
          count += 1;
        }
      }
      // Calculate total count (only unread notifications)
      combinedNotifications.totalCount = count + combinedNotifications.chatNotifications.filter((notification) => !notification.read).length;

      setNotifications(combinedNotifications);
    } catch (error) {
      // Error is handled silently - notifications will just not update
    } finally {
      setLoading(false);
    }
  }, [currentUserId]);

  // Mark general notifications as read
  const markGeneralNotificationsAsRead = useCallback(async (notificationIds: number[]) => {
    try {
      await markNotificationsAsRead(notificationIds);
      // Update notifications to mark them as read in state
      setNotifications((prev) => {
        const unreadCount = notificationIds.filter((id) => prev.generalNotifications.find((n) => n.id === id && !n.is_read)).length;

        return {
          ...prev,
          generalNotifications: prev.generalNotifications.map((notification) => (notificationIds.includes(notification.id) ? { ...notification, is_read: true } : notification)),
          totalCount: prev.totalCount - unreadCount,
        };
      });
    } catch (error) {
      // Error is handled silently - state will just not update
    }
  }, []);

  // Mark chat notifications as read
  const markChatNotificationsAsRead = useCallback(
    async (artifactIds: string[]) => {
      try {
        await markChatAsRead(artifactIds, currentUserId);
        // Update marked notifications as read in state
        setNotifications((prev) => {
          const unreadCount = artifactIds.filter((id) => prev.chatNotifications.find((n) => n.artifact_id === id && !n.read)).length;

          return {
            ...prev,
            chatNotifications: prev.chatNotifications.map((notification) => (artifactIds.includes(notification.artifact_id) ? { ...notification, read: true } : notification)),
            totalCount: prev.totalCount - unreadCount,
          };
        });
      } catch (error) {
        // Error is handled silently - state will just not update
      }
    },
    [currentUserId],
  );

  // Mark all notifications as read
  const markAllNotificationsAsRead = useCallback(async () => {
    try {
      const unreadGeneralNotificationIds = notifications.generalNotifications.filter((n) => !n.is_read).map((n) => n.id);
      const unreadChatArtifactIds = notifications.chatNotifications.filter((n) => !n.read).map((n) => n.artifact_id);

      // Mark all unread notifications as read in parallel
      await Promise.all([
        unreadGeneralNotificationIds.length > 0 ? markGeneralNotificationsAsRead(unreadGeneralNotificationIds) : Promise.resolve(),
        unreadChatArtifactIds.length > 0 ? markChatNotificationsAsRead(unreadChatArtifactIds) : Promise.resolve(),
      ]);
    } catch (error) {
      // Error is handled silently - notifications will just not update
    }
  }, [notifications, markGeneralNotificationsAsRead, markChatNotificationsAsRead]);

  useEffect(() => {
    fetchAllNotifications();
  }, [fetchAllNotifications]);

  useEffect(() => {
    if (!currentUserId) {
      return () => {
        // No cleanup needed when currentUserId is not available
      };
    }
    const pollInterval = setInterval(() => {
      fetchAllNotifications();
    }, 15000);
    return () => {
      clearInterval(pollInterval);
    };
  }, [currentUserId, fetchAllNotifications]);

  // Enhanced socket listeners for real-time updates
  useEffect(() => {
    if (!currentUserId) {
      return () => {
        // No cleanup needed when currentUserId is not available
      };
    }

    const handleNewMessage = () => {
      fetchAllNotifications();
    };

    const handleNewNotification = () => {
      fetchAllNotifications();
    };

    const handleNotificationSent = () => {
      fetchAllNotifications();
    };

    // Listen for socket connection
    const handleConnect = () => {
      socket.emit('join', currentUserId);
    };

    // Join user room when socket connects
    if (socket.connected) {
      socket.emit('join', currentUserId);
    }

    socket.on('connect', handleConnect);
    socket.on('newMessage', handleNewMessage);
    socket.on('newNotification', handleNewNotification);
    socket.on('notificationSent', handleNotificationSent);

    return () => {
      socket.off('connect', handleConnect);
      socket.off('newMessage', handleNewMessage);
      socket.off('newNotification', handleNewNotification);
      socket.off('notificationSent', handleNotificationSent);
    };
  }, [currentUserId, fetchAllNotifications]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && currentUserId) {
        fetchAllNotifications();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [currentUserId, fetchAllNotifications]);

  return {
    notifications,
    loading,
    fetchAllNotifications,
    markGeneralNotificationsAsRead,
    markChatNotificationsAsRead,
    markAllNotificationsAsRead,
  };
};
